/**
 * Utilidad para enviar eventos a Meta Conversions API (CAPI)
 * Envía eventos al backend de Laravel que los procesa y envía a Meta
 */

import { getApiUrl, buildUserData, generateEventId } from './metaHelpers';

/**
 * Envía un evento a Meta Conversions API a través del backend
 * @param {string} endpoint - Endpoint del backend (ej: 'pageview', 'add-to-cart')
 * @param {object} eventData - Datos del evento
 * @param {object} user - Objeto de usuario (opcional)
 * @returns {Promise<object>} - Respuesta del backend
 */
async function sendMetaEvent(endpoint, eventData = {}, user = null) {
  const apiUrl = getApiUrl();
  
  if (!apiUrl) {
    console.warn('Meta CAPI: BASE_URL no está configurado');
    return null;
  }

  try {
    // Construir user_data automáticamente
    const userData = buildUserData(user);

    // Agregar event_source_url y event_time si no están presentes
    const finalEventData = {
      ...eventData,
      event_source_url: eventData.event_source_url || (typeof window !== 'undefined' ? window.location.href : ''),
      event_time: eventData.event_time || Math.floor(Date.now() / 1000),
      user_data: {
        ...userData,
        ...(eventData.user_data || {}), // Permitir sobrescribir si se pasa explícitamente
      },
    };

    // Debug log para verificar deduplicación
    console.log(`[Meta Debug] CAPI ${endpoint}:`, {
      event_id: finalEventData.event_id,
      event_name: finalEventData.event_name || endpoint,
      event_time: finalEventData.event_time,
    });

    const response = await fetch(`${apiUrl}/meta/events/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(finalEventData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Meta CAPI Error (${endpoint}):`, {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
      });
      return null;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Meta CAPI Error (${endpoint}):`, error);
    return null;
  }
}

/**
 * Envía un evento PageView a Meta Conversions API
 * @param {object} options - Opciones del evento
 * @param {string} options.event_source_url - URL de la página (opcional, se obtiene automáticamente)
 * @param {string} options.event_id - Event ID para deduplicación (opcional, debe ser el mismo que el Pixel)
 * @param {object} options.user - Usuario logueado (opcional)
 * @returns {Promise<object>} - Respuesta del backend
 */
export async function sendPageView(options = {}) {
  // Usar event_id proporcionado externamente o generar uno nuevo
  const eventId = options.event_id || generateEventId('pageview');
  
  return await sendMetaEvent('pageview', {
    event_id: eventId,
    event_source_url: options.event_source_url,
  }, options.user);
}

/**
 * Envía un evento ViewCategory a Meta Conversions API
 * @param {string} categoryName - Nombre de la categoría
 * @param {object} options - Opciones adicionales
 * @param {object} options.user - Usuario logueado (opcional)
 * @returns {Promise<object>} - Respuesta del backend
 */
export async function sendViewCategory(categoryName, options = {}) {
  const eventId = generateEventId('viewcategory', categoryName);
  
  return await sendMetaEvent('view-category', {
    event_id: eventId,
    content_category: categoryName,
  }, options.user);
}

/**
 * Envía un evento AddToCart a Meta Conversions API
 * @param {object} product - Datos del producto
 * @param {string} product.id - ID del producto
 * @param {string} product.sku - SKU del producto
 * @param {string} product.name - Nombre del producto
 * @param {number} product.price - Precio del producto
 * @param {number} product.quantity - Cantidad (opcional)
 * @param {string} product.currency - Moneda (opcional, default: 'CLP')
 * @param {string} eventId - Event ID para deduplicación (debe ser el mismo que Pixel)
 * @param {object} options - Opciones adicionales
 * @param {object} options.user - Usuario logueado (opcional)
 * @returns {Promise<object>} - Respuesta del backend
 */
export async function sendAddToCart(product, eventId = null, options = {}) {
  const finalEventId = eventId || generateEventId('addtocart', product.id || product.sku);
  
  return await sendMetaEvent('add-to-cart', {
    event_id: finalEventId,
    content_ids: [product.id || product.sku],
    content_name: product.name,
    value: product.price || product.sale_price || product.cost || 0,
    currency: product.currency || 'CLP',
    quantity: product.quantity || 1,
    content_category: product.categories?.[0]?.name || product.category || null,
  }, options.user);
}

/**
 * Envía un evento InitiateCheckout a Meta Conversions API
 * @param {number} total - Total del carrito
 * @param {number} numItems - Número de items en el carrito
 * @param {Array} contentIds - IDs de los productos (opcional)
 * @param {string} eventId - Event ID para deduplicación (debe ser el mismo que Pixel)
 * @param {object} options - Opciones adicionales
 * @param {string} options.currency - Moneda (opcional, default: 'CLP')
 * @param {object} options.user - Usuario logueado (opcional)
 * @returns {Promise<object>} - Respuesta del backend
 */
export async function sendInitiateCheckout(total, numItems, contentIds = [], eventId = null, options = {}) {
  const finalEventId = eventId || generateEventId('initiatecheckout');
  
  return await sendMetaEvent('initiate-checkout', {
    event_id: finalEventId,
    value: total,
    currency: options.currency || 'CLP',
    num_items: numItems,
    content_ids: contentIds.length > 0 ? contentIds : undefined,
  }, options.user);
}

/**
 * Envía un evento Search a Meta Conversions API
 * @param {string} searchTerm - Término de búsqueda
 * @param {string} eventId - Event ID para deduplicación (debe ser el mismo que Pixel)
 * @param {object} options - Opciones adicionales
 * @param {object} options.user - Usuario logueado (opcional)
 * @returns {Promise<object>} - Respuesta del backend
 */
export async function sendSearch(searchTerm, eventId = null, options = {}) {
  const finalEventId = eventId || generateEventId('search');
  
  return await sendMetaEvent('search', {
    event_id: finalEventId,
    search_string: searchTerm,
  }, options.user);
}

/**
 * Envía un evento CompleteRegistration a Meta Conversions API
 * @param {object} userData - Datos del usuario registrado
 * @param {string} userData.email - Email del usuario
 * @param {string} userData.id - ID del usuario
 * @param {string} userData.phone - Teléfono (opcional)
 * @param {string} userData.first_name - Nombre (opcional)
 * @param {string} userData.last_name - Apellido (opcional)
 * @param {string} eventId - Event ID para deduplicación (debe ser el mismo que Pixel)
 * @param {object} options - Opciones adicionales
 * @returns {Promise<object>} - Respuesta del backend
 */
export async function sendCompleteRegistration(userData, eventId = null, options = {}) {
  const finalEventId = eventId || generateEventId('registration', userData.id);
  
  return await sendMetaEvent('complete-registration', {
    event_id: finalEventId,
    status: true,
    user_data: {
      email: userData.email,
      phone: userData.phone,
      first_name: userData.first_name || userData.name?.split(' ')[0],
      last_name: userData.last_name || userData.name?.split(' ').slice(1).join(' '),
      external_id: userData.id?.toString(),
    },
  }, userData);
}

/**
 * Envía un evento Purchase a Meta Conversions API
 * @param {object} orderData - Datos de la orden
 * @param {number} orderData.total - Total de la orden
 * @param {string|number} orderData.order_id - ID de la orden
 * @param {Array} orderData.products - Array de productos
 * @param {string} eventId - Event ID para deduplicación (debe ser el mismo que Pixel)
 * @param {object} options - Opciones adicionales
 * @param {string} options.currency - Moneda (opcional, default: 'CLP')
 * @param {object} options.user - Usuario logueado (opcional)
 * @returns {Promise<object>} - Respuesta del backend
 */
export async function sendPurchase(orderData, eventId = null, options = {}) {
  const finalEventId = eventId || generateEventId('purchase', orderData.order_id);
  
  const productIds = orderData.products?.map(p => p.id || p.sku || p.product_id) || [];
  
  return await sendMetaEvent('purchase', {
    event_id: finalEventId,
    value: orderData.total,
    currency: options.currency || 'CLP',
    content_ids: productIds.length > 0 ? productIds : undefined,
    num_items: orderData.products?.length || 0,
  }, options.user || orderData.user);
}

/**
 * Envía un evento personalizado a Meta Conversions API
 * @param {string} eventName - Nombre del evento personalizado
 * @param {object} eventData - Datos del evento
 * @param {string} eventData.event_id - Event ID (opcional, se genera automáticamente)
 * @param {object} options - Opciones adicionales
 * @param {object} options.user - Usuario logueado (opcional)
 * @returns {Promise<object>} - Respuesta del backend
 */
export async function sendCustomEvent(eventName, eventData = {}, options = {}) {
  const eventId = eventData.event_id || generateEventId(eventName.toLowerCase());
  
  return await sendMetaEvent('', {
    event_name: eventName,
    event_id: eventId,
    ...eventData,
  }, options.user);
}